import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import moment from 'moment';
import 'moment-timezone';
import { Post } from './Post';
import controller from '@/controller';

const fb = require('../firebase');

const getDailyRetweetLimitMessage = (limit) =>
  `You can retweet up to ${limit} times per day. Try again tomorrow.`;

// This file is a duplicate of '/functions/src/models/Retweet.js'
class Retweet extends Post {
  constructor(id, tweetId, time, user, scheduled, createdAt, publishingError, retweetId,
              originalTweetInfo, deleted, conditionalRetweetsConditions) {
    super(id, time, scheduled, user, publishingError, deleted, conditionalRetweetsConditions);
    this.tweetId = tweetId;
    this.createdAt = createdAt;
    this.type = 'retweet';
    this.retweetId = retweetId;
    this.originalTweetInfo = originalTweetInfo;
    this.conditionalRetweetsConditions = conditionalRetweetsConditions;
  }

  static newRetweet(tweetId, time, user, originalTweetInfo) {
    return new Retweet(null, tweetId, time, user, null, null, null,
      null, originalTweetInfo, false, null);
  }

  static async assertDailyLimitForTimes(firebaseUser, user, times) {
    const timesByLocalDate = times.reduce((result, time) => {
      const localDate = moment.tz(time, user.timezone).format('YYYY-MM-DD');

      if (!result[localDate]) {
        result[localDate] = [];
      }

      result[localDate].push(time);
      return result;
    }, {});

    const localDates = Object.keys(timesByLocalDate);

    for (const localDate of localDates) {
      const time = timesByLocalDate[localDate][0];
      const usage = await controller.twitter.getDailyRetweetLimit(
        firebaseUser,
        user.uid,
        moment(time).toISOString()
      );

      if (usage.remaining < timesByLocalDate[localDate].length) {
        throw new Error(getDailyRetweetLimitMessage(usage.limit));
      }
    }
  }

  async saveToFirestore(eventStore, firebaseUser) {
    if (firebaseUser) {
      await Retweet.assertDailyLimitForTimes(firebaseUser, this.user, [this.time]);
    }

    return fb.threadsCollection
      .add({
        tweetId: this.tweetId,
        time: this._timeForFirestore(),
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        scheduled: false,
        user: fb.usersCollection.doc(this.user.uid),
        type: 'retweet',
        originalTweetInfo: this.originalTweetInfo,
        deleted: false,
        publishingError: null,
        postNow: false,
        conditionalRetweetsConditions: null,
      }).then((result) => {
        eventStore.scheduleRetweet();
        return result;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  _timeForFirestore() {
    return moment(this.time).toDate();
  }

  update() {
    if (!this.id) throw Error('This retweet wasn\'t saved, so it can\'t be updated.');

    const updateMap = {
      tweetId: this.tweetId,
      time: this._timeForFirestore(),
      scheduled: this.scheduled,
      updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      deleted: this.deleted,
      originalTweetInfo: this.originalTweetInfo,
      growthProgramIsValidated: this.growthProgramIsValidated ? this.growthProgramIsValidated : null,
    };

    return fb.threadsCollection.doc(this.id).update(updateMap)
      .catch(error => {
        console.error(error);
        alert('An error has occurred while updating the retweet.')
      });
  }

  hasErrors() {
    if (this.publishingError) return true;

    return false;
  }

  isEmpty() {
    return false;
  }

  couldNotBePostedBecauseNotPremium() {
    return this.hasErrors() && this.publishingError[0].message &&
      this.publishingError[0].message.includes('Only premium');
  }

  doPostNow(firebaseUser, currentUserId) {
    if (!this.id) throw Error("This retweet wasn't saved, so it can't be updated.");
    return controller.posts.postNow(firebaseUser, currentUserId, this.id).catch((error) => {
      console.error(error);
      alert('An error has occurred while instantly posting the retweet.');
    });
  }

  static buildFromFirestore(doc, timezone) {
    return new Retweet(doc.id,
      doc.data().tweetId,
      moment.tz(doc.data().time.seconds * 1000, timezone),
      doc.data().user,
      doc.data().scheduled,
      doc.data().createdAt,
      doc.data().publishingError,
      doc.data().retweetId,
      doc.data().originalTweetInfo,
      doc.data().deleted,
      doc.data().conditionalRetweetsConditions)
  }

}

export { Retweet };
